/**
 * CoachPlaybook - Player Focus Row Component
 * Version: 0.5.145
 * 
 * A compact row of player icons for selection and spotlight control.
 * 
 * v0.5.139 Changes:
 * - Added isTablet prop for larger touch targets
 * - Tablet: 52px icons, more padding
 * - Tablet: Floating bar style
 * 
 * v0.4.4 Changes:
 * - Unified selection: selectedPosition + onPositionSelect props
 * - Small styled clear focus button
 * - Fixed layout to prevent court overlap
 * - Selection ring separate from spotlight
 */

import React from 'react';

const PlayerFocusRow = ({
  positions = [],
  spotlightPlayer = null,
  selectedPosition = null,
  currentBallHolder = null,
  onSpotlightChange,
  onPositionSelect,
  isAnimating = false,
  isTablet = false
}) => {
  const placedPlayers = positions.filter(p => p.startX !== null && p.startX !== undefined);
  
  const handlePlayerClick = (playerId) => {
    // Always select the player
    onPositionSelect?.(playerId);
    
    // Toggle spotlight
    if (spotlightPlayer === playerId) {
      onSpotlightChange?.(null);
    } else {
      onSpotlightChange?.(playerId);
    }
  };
  
  const hasFocus = spotlightPlayer !== null;
  
  // Tablet sizes
  const iconSize = isTablet ? 52 : 36;
  const fontSize = isTablet ? 14 : 11;
  const padding = isTablet ? '10px 16px' : '6px 10px';
  const gap = isTablet ? '10px' : '6px';
  
  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: gap,
      padding: padding,
      backgroundColor: isTablet ? 'rgba(31, 41, 55, 0.95)' : '#1f2937',
      borderRadius: isTablet ? '16px' : '6px',
      flexShrink: 0,
      flexWrap: 'nowrap',
      minHeight: isTablet ? '72px' : '52px',
      boxShadow: isTablet ? '0 8px 32px rgba(0,0,0,0.4)' : 'none',
      backdropFilter: isTablet ? 'blur(8px)' : 'none'
    },
    clearButton: {
      padding: isTablet ? '8px 14px' : '4px 8px',
      borderRadius: isTablet ? '8px' : '4px',
      border: hasFocus ? '1px solid #fbbf24' : '1px solid #4b5563',
      backgroundColor: hasFocus ? '#fef3c7' : '#374151',
      color: hasFocus ? '#92400e' : '#6b7280',
      fontSize: isTablet ? '13px' : '10px',
      fontWeight: '600',
      cursor: hasFocus ? 'pointer' : 'default',
      whiteSpace: 'nowrap',
      opacity: hasFocus ? 1 : 0.5,
      flexShrink: 0,
      minHeight: isTablet ? '44px' : 'auto'
    },
    divider: {
      width: '1px',
      height: isTablet ? '36px' : '24px',
      backgroundColor: '#4b5563'
    },
    playerButton: {
      width: `${iconSize}px`,
      height: `${iconSize}px`,
      borderRadius: '50%',
      border: '3px solid transparent',
      color: '#fff',
      fontSize: `${fontSize}px`,
      fontWeight: 'bold',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      transition: 'all 0.15s ease',
      flexShrink: 0
    },
    ballIndicator: {
      position: 'absolute',
      top: isTablet ? '-4px' : '-3px',
      right: isTablet ? '-4px' : '-3px',
      fontSize: isTablet ? '14px' : '10px'
    },
    emptyState: {
      color: '#6b7280',
      fontSize: isTablet ? '14px' : '11px'
    }
  };
  
  return (
    <div style={styles.container}>
      {/* Clear Focus Button - small and styled */}
      <button
        onClick={() => {
          onSpotlightChange?.(null);
        }}
        disabled={!hasFocus}
        style={styles.clearButton}
        title={hasFocus ? "Clear focus" : "No focus active"}
      >
        {hasFocus ? '✕ Clear' : '👁 All'}
      </button>
      
      <div style={styles.divider} />
      
      {placedPlayers.length === 0 ? (
        <span style={styles.emptyState}>No players placed</span>
      ) : (
        placedPlayers.map((player) => {
          const isSpotlit = spotlightPlayer === player.id;
          const isSelected = selectedPosition === player.id;
          const hasBall = currentBallHolder === player.id;
          
          return (
            <button
              key={player.id}
              onClick={() => handlePlayerClick(player.id)}
              style={{
                ...styles.playerButton,
                backgroundColor: player.color || '#374151',
                border: isSelected 
                  ? `3px solid #3b82f6`  // Blue for selection
                  : isSpotlit 
                    ? `3px solid #fbbf24`  // Gold for spotlight
                    : '3px solid transparent',
                boxShadow: isSpotlit 
                  ? '0 0 8px rgba(251, 191, 36, 0.5)' 
                  : isSelected
                    ? '0 0 8px rgba(59, 130, 246, 0.5)'
                    : '0 2px 4px rgba(0,0,0,0.3)',
                opacity: spotlightPlayer && !isSpotlit ? 0.35 : 1,
                transform: isSelected ? 'scale(1.1)' : 'scale(1)'
              }}
              title={`${player.positionLabel}${isSpotlit ? ' (focused)' : ''}${isSelected ? ' (selected)' : ''}`}
            >
              {player.jerseyNumber || player.positionLabel}
              
              {hasBall && (
                <span style={styles.ballIndicator}>🏀</span>
              )}
            </button>
          );
        })
      )}
    </div>
  );
};

export default PlayerFocusRow;
